import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { v1 as uuid } from 'uuid';
import type { IconName } from '../../components/Icons/IconNames';
import type { NotificationVariants } from '../../components/Notification/types';
import { EMPTY_OBJECT } from '../../utils/empty';

export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface ToastProps extends ToastPropsBase {
  text: ReactNode;
  variant?: NotificationVariants;
  /** Replaces the status dot. */
  icon?: IconName;
}

export type ToastPropsBase = {
  id?: string;
  /** Toast Timeout (undefined for default, null for no timeout) */
  timeout?: number | null; // null for no timeout
  /** is X shown to remove */
  dismissable?: boolean;
};

type UseToastArgs = {
  defaultProps: ToastPropsBase;
};

/**
 * Local toast list state. Auto-dismiss timers are owned by `<Toasts>` via
 * `useToastsAutoDismiss` — callers must render `<Toasts toasts={...} remove={...} />`
 * (or pass the list to a child that does). Prefer `useGlobalToasts` when there is
 * no local toast host.
 */
export function useToasts<T extends ToastPropsBase = ToastProps>(args?: UseToastArgs) {
  const [toasts, setToasts] = useState<T[]>([]);
  const toastDefaultProps = args?.defaultProps ?? EMPTY_OBJECT;

  const remove = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const add = useCallback(
    (toast: T): string => {
      if (toast.id == null) {
        toast.id = uuid();
      }
      const newToast = { ...toastDefaultProps, ...toast } as T & { id: string };
      setToasts(prev => [...prev, newToast]);
      return newToast.id;
    },
    [toastDefaultProps]
  );

  return useMemo(
    () => ({
      toasts,
      add,
      remove,
      clearAll,
    }),
    [toasts, add, remove, clearAll]
  );
}
